import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { WhatsAppButton } from "../home/CtaButtons";
import { imageUrl, trackEvent } from "../../lib/api";
import { quoteHref } from "../../data/homeContent";
import { doorOnderwerp, labelsOf, materialLabel, srcSetOf } from "../../lib/catalog";

const Picker = ({ name, title, ids, opts, value, onChange, swatches }) => (
  <fieldset>
    <legend className="text-[13px] font-semibold text-ink">{title} <span className="font-normal text-slate-500">(optioneel)</span></legend>
    <div className="mt-2 flex flex-wrap gap-2" role="radiogroup">
      {ids.map((id) => {
        const on = value === id;
        return (
          <button key={id} type="button" role="radio" aria-checked={on} onClick={() => onChange(on ? null : id)} data-testid={`door-detail-${name}-${id}`}
            className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-3 text-[13.5px] transition-[border-color,background-color] duration-150 ${on ? "border-maas bg-maas-soft font-semibold text-maas-deep" : "border-slate-200 bg-white text-ink hover:border-slate-300"}`}>
            {swatches && <span className={`h-4 w-4 rounded-full ring-1 ring-inset ring-black/15 ${opts[id]?.swatch ? "" : "bg-[conic-gradient(#e11d48,#f59e0b,#16a34a,#2563eb,#9333ea,#e11d48)]"}`}
              style={opts[id]?.swatch ? { backgroundColor: opts[id].swatch } : undefined} aria-hidden="true" />}
            {opts[id]?.label}
            {on && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
  </fieldset>
);

const Photos = ({ door }) => {
  const all = [door.image, ...door.gallery];
  const [cur, setCur] = useState(all[0]);
  return (
    <div>
      <div className="relative overflow-hidden rounded-2xl bg-slate-100">
        <img key={cur} src={imageUrl(cur)} srcSet={srcSetOf(cur)} sizes="(min-width: 768px) 480px, 100vw" alt={door.image_alt}
          className="mx-auto block h-auto max-h-[72vh] w-full object-contain" data-testid="door-detail-image" />
        {door.image_kind === "inspiratie" && <span className="badge-sfeer">Inspiratiebeeld</span>}
      </div>
      {all.length > 1 && (
        <ul className="mt-3 flex gap-2 overflow-x-auto">
          {all.map((p, i) => (
            <li key={p}><button type="button" onClick={() => setCur(p)} data-testid={`door-detail-thumb-${i}`} aria-label={`Foto ${i + 1}`}
              className={`h-16 w-14 overflow-hidden rounded-lg ring-2 ${cur === p ? "ring-maas" : "ring-transparent"}`}><img src={imageUrl(p)} alt="" className="h-full w-full object-cover" /></button></li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DetailBody = ({ door, opts }) => {
  const { pathname } = useLocation();
  const [color, setColor] = useState(null);
  const [glass, setGlass] = useState(null);
  const chips = [...labelsOf(door.types, opts), ...labelsOf(door.styles, opts), door.has_glass ? "Met glas" : "Zonder glas"];
  const href = quoteHref(doorOnderwerp(door), { deur: door.id, kleur: color, glas: glass });
  return (
    <div className="grid gap-6 md:grid-cols-2 md:gap-8">
      <Photos door={door} />
      <div className="flex flex-col">
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">{materialLabel(door.material)}{door.supplier ? ` · ${door.supplier}` : ""}{door.model_name ? ` · ${door.model_name}` : ""}</p>
        <DialogTitle className="mt-2 font-display text-[24px] font-bold leading-tight text-ink" data-testid="door-detail-title">{door.title}</DialogTitle>
        <ul className="mt-3 flex flex-wrap gap-1.5">{chips.map((c) => <li key={c} className="chip">{c}</li>)}</ul>
        <DialogDescription className="mt-4 text-[15px] leading-relaxed text-slate-600">
          {door.description || "Deze deur maken wij op maat, inclusief kozijn, hang- en sluitwerk en afwerking."}
        </DialogDescription>
        <div className="mt-6 space-y-5">
          {door.colors.length > 0 && <Picker name="kleur" title="Kies een kleur" ids={door.colors} opts={opts} value={color} onChange={setColor} swatches />}
          {door.has_glass && door.glass_options.length > 0 && <Picker name="glas" title="Kies de glasuitvoering" ids={door.glass_options} opts={opts} value={glass} onChange={setGlass} />}
        </div>
        {door.specs.length > 0 && (
          <dl className="mt-6 divide-y divide-slate-100 rounded-xl border border-slate-200 text-[14px]" data-testid="door-detail-specs">
            {door.specs.map((s) => <div key={s.label} className="flex justify-between gap-4 px-4 py-2.5"><dt className="text-slate-500">{s.label}</dt><dd className="text-right font-medium text-ink">{s.value}</dd></div>)}
          </dl>
        )}
        <div className="mt-7 grid gap-3">
          <Link to={href} state={{ from: pathname }} className="btn-cta" data-testid="door-detail-quote"
            onClick={() => trackEvent("cta_quote_click", { location: "door-detail", door: door.id })}>
            Ontvang een prijsindicatie <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </Link>
          <WhatsAppButton location="door-detail" label="Vraag het via WhatsApp" />
        </div>
        <p className="mt-4 text-[12.5px] leading-relaxed text-slate-500">
          {door.image_kind === "inspiratie" ? "Inspiratiebeeld. De exacte uitvoering, maten en mogelijkheden stemmen we met u af bij het inmeten." : "Afgebeelde uitvoering kan afwijken in maat en kleur. Wij adviseren u graag bij het inmeten."}
        </p>
      </div>
    </div>
  );
};

export const DoorDetailDialog = ({ door, opts, onClose }) => {
  const [last, setLast] = useState(door);
  useEffect(() => { if (door) setLast(door); }, [door]);
  const shown = door || last;
  return (
    <Dialog open={!!door} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto rounded-3xl p-5 sm:p-8 [&>button:last-child]:hidden" data-testid="door-detail-dialog">
        <DialogClose className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-600 ring-1 ring-slate-200 hover:text-ink" data-testid="door-detail-close">
          <X className="h-5 w-5" aria-hidden="true" /><span className="sr-only">Sluiten</span>
        </DialogClose>
        {shown && <DetailBody key={shown.id} door={shown} opts={opts} />}
      </DialogContent>
    </Dialog>
  );
};
